import { useCheckIsIdle } from '@/context/services/hooks/useCheckIsIdle';
import { useConvertToModernPagination } from '@/context/services/hooks/usePagination';
import {
  ForbiddenCodeEnum,
  isAuthError,
  isForbiddenError,
  isInvalidError,
  isPaymentRequiredError,
  isSuccess,
} from '@/context/services/type';
import { TransactionItemDetailOptionsColumn } from '@/features/transaction_items/Detail/type';
import { isCommonError } from '@/libs/typeguard/isError';
import { getPageData } from '@/utils/getPageData';
import { ModernPaginationProps } from '@moneyforward/ap-frontend-components';
import {
  InfiniteData,
  QueryKey,
  UseSuspenseInfiniteQueryResult,
  useQueryClient,
} from '@tanstack/react-query';
import {
  ApGeneralInputOption,
  ApInvoiceTransactionGeneralInput,
  CommonError,
  GetApInvoiceTransactionGeneralInputOptions200,
  GetApInvoiceTransactionGeneralInputOptionsParams,
  UnauthorizedResponse,
  getGetApInvoiceTransactionGeneralInputOptionsQueryKey,
  useDeleteApGeneralInputOptions,
  useGetApInvoiceTransactionGeneralInputOption,
  useGetApInvoiceTransactionGeneralInputOptionHook,
  useGetApInvoiceTransactionGeneralInputOptionSuspense,
  useGetApInvoiceTransactionGeneralInputOptionsHook,
  useGetApInvoiceTransactionGeneralInputOptionsInfinite,
  useGetApInvoiceTransactionGeneralInputOptionsSuspense,
  useGetApInvoiceTransactionGeneralInputOptionsSuspenseInfinite,
  useGetApInvoiceTransactionGeneralInputSuspense,
  usePostApGeneralInputOptions,
  usePutApGeneralInputOptions,
} from 'ap-openapi';
import { AxiosResponse } from 'axios';
import { useCallback, useEffect, useMemo } from 'react';

type FetchTransactionItemsDetail = (id: string) => {
  data: ApInvoiceTransactionGeneralInput | undefined;
};

export type FetchTransactionItemDetailOptions = (
  id: string,
  params: GetApInvoiceTransactionGeneralInputOptionsParams
) => {
  data: TransactionItemDetailOptionsColumn[];
  pagination: ModernPaginationProps;
};

export type FetchTransactionItemDetailOption = (
  pulldownId: string,
  id: string,
  enabled: boolean
) => {
  data: ApGeneralInputOption | null;
  isLoading: boolean;
};

export type TransactionItemOptionsQuery =
  GetApInvoiceTransactionGeneralInputOptionsParams;

export const useGetApInvoiceTransactionGeneralInputOptions = () => {
  return useGetApInvoiceTransactionGeneralInputOptionsHook();
};

export const useGetTransactionItemDetail: FetchTransactionItemsDetail = (
  id
) => {
  const { data } = useGetApInvoiceTransactionGeneralInputSuspense(id);

  if (data && (isAuthError(data.status) || isForbiddenError(data.status))) {
    // @ts-ignore - data.data might not have code property in success types
    if (data.data?.code !== ForbiddenCodeEnum.FORBIDDEN_MIMIC_MODE)
      location.href = data.headers?.location ?? '';
  }

  return {
    data: data?.data?.ap_invoice_transaction_general_input,
  };
};

export const useGetTransactionItemDetailOptions: FetchTransactionItemDetailOptions =
  (id: string, params: GetApInvoiceTransactionGeneralInputOptionsParams) => {
    const { data } = useGetApInvoiceTransactionGeneralInputOptionsSuspense(
      id,
      params
    );

    if (data && (isAuthError(data.status) || isForbiddenError(data.status))) {
      location.href = data.headers?.location ?? '';
    }

    const pagination: ModernPaginationProps = useConvertToModernPagination(
      data?.data?.pagination
    );

    return {
      data: data.data.ap_general_input_options?.map(responseToColumn) ?? [],
      pagination: pagination,
    };
  };

export const useGetCustomPulldownOptionsAll = (id?: string) => {
  const checkIsIdle = useCheckIsIdle();

  const { data, error, fetchNextPage, hasNextPage, queryKey } =
    useGetApInvoiceTransactionGeneralInputOptionsInfinite(
      id || '',
      {
        per: 1000,
        is_visible: true,
      },
      {
        query: {
          enabled: id !== undefined,
          getNextPageParam: ({ request, data: { pagination } }) => {
            const { searchParams } = new URL(request.responseURL);
            const page = searchParams.get('page') || 1;

            if (pagination?.has_next_page) {
              return Number(page) + 1;
            }

            return undefined;
          },
          staleTime: Infinity,
        },
      }
    );

  if (error) throw error;
  useEffect(() => {
    if (hasNextPage && checkIsIdle(queryKey)) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, checkIsIdle, queryKey]);

  return useMemo(() => getPageData(data, 'ap_general_input_options'), [data]);
};

const responseToColumn = (
  item: ApGeneralInputOption
): TransactionItemDetailOptionsColumn => {
  return {
    id: item.id?.toString() ?? '',
    code: item.code ?? '',
    label: item.label ?? '',
    priority: item.priority ?? 0,
    is_visible: item.is_visible ?? false,
  };
};

export const useGetTransactionItemPulldownOption = () => {
  return useGetApInvoiceTransactionGeneralInputOptionHook();
};

export const useGetPulldownOptionDetailById = (
  apInvoiceTransactionGeneralInputId: string,
  id: string
) => {
  const { data } = useGetApInvoiceTransactionGeneralInputOptionSuspense(
    apInvoiceTransactionGeneralInputId,
    id
  );

  return {
    data: data?.data,
  };
};

export enum TransactionItemDetailOptionStatus {
  SUCCESS = 'success',
  INVALID = 'invalid',
  AUTH = 'auth',
  ERROR = 'error',
  PROXY_LOGIN_ERROR = 'proxy_login_error',
  CORPORATE_REQUIRED = 'corporate_required',
}

// Common types for all operations
export type ValidationErrorResponse = {
  errors: {
    [key: string]: {
      message: string;
      code: string;
    }[];
  };
};

type BaseOperationSuccess<T = unknown> = {
  status: TransactionItemDetailOptionStatus.SUCCESS;
  data: T;
};

type BaseOperationInvalidError = {
  status: TransactionItemDetailOptionStatus.INVALID;
  errors: ValidationErrorResponse;
};

type BaseOperationAuthError = {
  status: TransactionItemDetailOptionStatus.AUTH;
  errors: UnauthorizedResponse;
  redirectPath: string;
};

type BaseOperationCommonError = {
  status: TransactionItemDetailOptionStatus.ERROR;
  errors: CommonError;
};

type BaseOperationProxyLoginError = {
  status: TransactionItemDetailOptionStatus.PROXY_LOGIN_ERROR;
  errors: CommonError;
};

type BaseOperationCorporateRequiredError = {
  status: TransactionItemDetailOptionStatus.CORPORATE_REQUIRED;
  errors: CommonError;
};

type OperationResult<T = unknown> =
  | BaseOperationSuccess<T>
  | BaseOperationInvalidError
  | BaseOperationAuthError
  | BaseOperationCommonError
  | BaseOperationProxyLoginError
  | BaseOperationCorporateRequiredError;

// Specific operation result types
type CreateResult = OperationResult<ApGeneralInputOption>;
type UpdateResult = OperationResult<ApGeneralInputOption>;
type DeleteResult =
  | (Omit<BaseOperationSuccess<never>, 'data'> & {
      status: TransactionItemDetailOptionStatus.SUCCESS;
    })
  | BaseOperationAuthError
  | BaseOperationCommonError
  | BaseOperationProxyLoginError;

const handleOperationResponse = <T>(
  resp: {
    status: number;
    data?: { ap_general_input_option?: T } & Record<string, unknown>;
    headers?: { location?: string };
  },
  includeInvalidError = true
): OperationResult<T> => {
  if (isSuccess(resp.status)) {
    return {
      status: TransactionItemDetailOptionStatus.SUCCESS,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      data: resp.data?.ap_general_input_option ?? ({} as T),
    };
  } else if (isAuthError(resp.status) || isForbiddenError(resp.status)) {
    if (resp.data?.code === ForbiddenCodeEnum.FORBIDDEN_MIMIC_MODE) {
      return {
        status: TransactionItemDetailOptionStatus.PROXY_LOGIN_ERROR,
        errors: isCommonError(resp.data) ? resp.data : {},
      };
    }
    return {
      status: TransactionItemDetailOptionStatus.AUTH,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      errors: (resp.data ?? {}) as UnauthorizedResponse,
      redirectPath: resp.headers?.location ?? '/',
    };
  } else if (includeInvalidError && isInvalidError(resp.status)) {
    return {
      status: TransactionItemDetailOptionStatus.INVALID,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      errors: (resp.data ?? {}) as ValidationErrorResponse,
    };
  } else if (isPaymentRequiredError(resp.status)) {
    return {
      status: TransactionItemDetailOptionStatus.CORPORATE_REQUIRED,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      errors: (resp.data ?? {}) as CommonError,
    };
  } else {
    return {
      status: TransactionItemDetailOptionStatus.ERROR,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      errors: (resp.data ?? {}) as CommonError,
    };
  }
};

const handleDeleteResponse = (resp: {
  status: number;
  data?: unknown;
  headers?: { location?: string };
}): DeleteResult => {
  if (isSuccess(resp.status)) {
    return {
      status: TransactionItemDetailOptionStatus.SUCCESS,
    };
  } else if (isAuthError(resp.status) || isForbiddenError(resp.status)) {
    // @ts-ignore - resp.data might not have code property in success types
    if (resp.data?.code === ForbiddenCodeEnum.FORBIDDEN_MIMIC_MODE) {
      return {
        status: TransactionItemDetailOptionStatus.PROXY_LOGIN_ERROR,
        errors: isCommonError(resp.data) ? resp.data : {},
      };
    }
    return {
      status: TransactionItemDetailOptionStatus.AUTH,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      errors: (resp.data ?? {}) as UnauthorizedResponse,
      redirectPath: resp.headers?.location ?? '/',
    };
  } else {
    return {
      status: TransactionItemDetailOptionStatus.ERROR,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      errors: (resp.data ?? {}) as CommonError,
    };
  }
};

const useDataMutation = <TMutationArgs, TUserArgs, TResult>(
  apInvoiceTransactionGeneralInputId: string,
  mutationHook: (options?: {
    mutation?: {
      onSuccess?: () => void;
    };
  }) => {
    mutateAsync: (
      args: TMutationArgs
    ) => Promise<{ status: number; data?: Record<string, unknown> }>;
    isPending?: boolean;
  },
  argsTransformer: (userArgs: TUserArgs) => TMutationArgs,
  responseHandler: (resp: {
    status: number;
    data?: Record<string, unknown>;
  }) => TResult
) => {
  const query = useQueryClient();
  const mutationResult = mutationHook();

  const mutateAsync = useCallback(
    async (args: TUserArgs): Promise<TResult> => {
      const transformedArgs = argsTransformer(args);
      const resp = await mutationResult.mutateAsync(transformedArgs);
      const result = responseHandler(resp);

      // Only invalidate queries on actual success
      if (isSuccess(resp.status)) {
        query.invalidateQueries({
          queryKey: getGetApInvoiceTransactionGeneralInputOptionsQueryKey(
            apInvoiceTransactionGeneralInputId
          ),
        });
      }

      return result;
    },
    [
      mutationResult,
      responseHandler,
      argsTransformer,
      query,
      apInvoiceTransactionGeneralInputId,
    ]
  );

  return { mutateAsync };
};

const useDeleteMutation = <TMutationArgs, TUserArgs, TResult>(
  id: string,
  mutationHook: (options?: {
    mutation?: {
      onSuccess?: () => void;
    };
  }) => {
    mutateAsync: (
      args: TMutationArgs
    ) => Promise<{ status: number; data?: unknown }>;
    isPending?: boolean;
  },
  argsTransformer: (userArgs: TUserArgs) => TMutationArgs,
  responseHandler: (resp: { status: number; data?: unknown }) => TResult
) => {
  const query = useQueryClient();
  const mutationResult = mutationHook();

  const mutateAsync = useCallback(
    async (args: TUserArgs): Promise<TResult> => {
      const transformedArgs = argsTransformer(args);
      const resp = await mutationResult.mutateAsync(transformedArgs);
      const result = responseHandler(resp);

      // Only invalidate queries on actual success
      if (isSuccess(resp.status)) {
        query.invalidateQueries({
          queryKey: getGetApInvoiceTransactionGeneralInputOptionsQueryKey(id),
        });
      }

      return result;
    },
    [mutationResult, responseHandler, argsTransformer, query, id]
  );

  return {
    mutateAsync,
    isLoading: mutationResult.isPending ?? false,
  };
};

type CreateTransactionItemDetailOptionMutateArgs = Parameters<
  ReturnType<typeof usePostApGeneralInputOptions>['mutateAsync']
>[0];

export type CreateTransactionItemDetailOptionUnprocessableEntityResponse =
  ValidationErrorResponse;

export const useCreateTransactionItemDetailOption = (
  apInvoiceTransactionGeneralInputId: string
) => {
  return useDataMutation<
    CreateTransactionItemDetailOptionMutateArgs,
    {
      data: {
        code: string;
        label: string;
        priority: number;
        is_visible: boolean;
      };
    },
    CreateResult
  >(
    apInvoiceTransactionGeneralInputId,
    usePostApGeneralInputOptions,
    (args) => ({
      id: apInvoiceTransactionGeneralInputId,
      data: {
        code: args.data.code ?? '',
        label: args.data.label ?? '',
        priority: args.data.priority ?? 0,
        is_visible: args.data.is_visible ?? false,
      },
    }),
    (resp) => handleOperationResponse<ApGeneralInputOption>(resp)
  );
};

type UpdateTransactionItemDetailOptionMutateArgs = Parameters<
  ReturnType<typeof usePutApGeneralInputOptions>['mutateAsync']
>[0];

// Maintain backward compatibility for exported types
export type UpdateTransactionItemDetailOptionUnprocessableEntityResponse =
  ValidationErrorResponse;

export const useUpdateTransactionItemDetailOption = (
  apInvoiceTransactionGeneralInputId: string
) => {
  return useDataMutation<
    UpdateTransactionItemDetailOptionMutateArgs,
    {
      id: string;
      data: {
        code: string;
        label: string;
        priority: number;
        is_visible: boolean;
      };
    },
    UpdateResult
  >(
    apInvoiceTransactionGeneralInputId,
    usePutApGeneralInputOptions,
    (args) => ({
      apInvoiceTransactionGeneralInputId,
      id: args.id,
      data: {
        code: args.data.code ?? '',
        label: args.data.label ?? '',
        priority: args.data.priority ?? 0,
        is_visible: args.data.is_visible ?? false,
      },
    }),
    (resp) => handleOperationResponse<ApGeneralInputOption>(resp)
  );
};

export const useDeleteTransactionItemDetailOption = (
  apInvoiceTransactionGeneralInputId: string
) => {
  return useDeleteMutation<
    { apInvoiceTransactionGeneralInputId: string; id: string },
    string,
    DeleteResult
  >(
    apInvoiceTransactionGeneralInputId,
    useDeleteApGeneralInputOptions,
    (id) => ({
      apInvoiceTransactionGeneralInputId,
      id,
    }),
    handleDeleteResponse
  );
};

export const useGetTransactionItemGeneralInputOptionsSuspenseInfinite = (
  id: string,
  params: GetApInvoiceTransactionGeneralInputOptionsParams
): UseSuspenseInfiniteQueryResult<
  InfiniteData<
    AxiosResponse<GetApInvoiceTransactionGeneralInputOptions200, unknown>,
    number | undefined
  >,
  CommonError
> & {
  queryKey: QueryKey;
} => {
  return useGetApInvoiceTransactionGeneralInputOptionsSuspenseInfinite(
    id,
    params,
    {
      query: {
        getNextPageParam: (page) => {
          const pagination = page?.data?.pagination;
          if (!pagination?.has_next_page || pagination.page === null)
            return undefined;
          return (pagination.page ?? 0) + 1;
        },
      },
    }
  );
};

export const useGetTransactionItemGeneralInputOption: FetchTransactionItemDetailOption =
  (pulldownId: string, id: string, enabled: boolean = true) => {
    const { data, isLoading } = useGetApInvoiceTransactionGeneralInputOption(
      pulldownId,
      id,
      {
        query: {
          enabled: enabled,
        },
      }
    );

    if (isSuccess(data?.status)) {
      return {
        data: data?.data ?? null,
        isLoading: isLoading,
      };
    } else {
      return {
        data: null,
        isLoading: isLoading,
      };
    }
  };
